import {
  BreakDetails,
  BreakKind,
  BreakList2,
  BreakListBreak,
  BreakListHoursOccurrences,
  BreakListOccurrence,
  BreakPlayType,
} from '@radioking/break';
import moment from 'moment-timezone';

export interface BreakApiList {
  color: string;
  name: string;
}

export interface BreakApiObj {
  [key: number]: BreakApiList;
}

export interface BreakOccurrenceApi {
  idbreak: number;
  idoccurrence: number;
  startdate: string;
  priority: number;
}

export interface OccurrenceApiDays {
  1: BreakOccurrenceApi[];
  2: BreakOccurrenceApi[];
  3: BreakOccurrenceApi[];
  4: BreakOccurrenceApi[];
  5: BreakOccurrenceApi[];
  6: BreakOccurrenceApi[];
  7: BreakOccurrenceApi[];
}

export interface OccurrenceApiObj {
  [key: string]: OccurrenceApiDays;
}

export interface BreaksApi {
  status: string;
  data: {
    breaks: BreakApiObj;
    occurrences: OccurrenceApiObj;
    count: number;
    limit: number;
    offset: number;
  };
}

export function convertToBreakList(breaksApi: BreaksApi): BreakList2 {
  const allBreaks: BreakListBreak[] = [];
  Object.entries(breaksApi.data.breaks).forEach(([id, data]) => {
    allBreaks.push({ id: Number.parseInt(id, 10), name: data.name, color: data.color });
  });

  const allOccurrences: BreakListHoursOccurrences[] = [];
  Object.entries(breaksApi.data.occurrences).forEach(([time, data]) => {
    const timeAllOccurrences: BreakListOccurrence[][] = [];
    Object.values(data).forEach(occ => {
      if (occ.length) {
        const timeDayAllOccurrences: BreakListOccurrence[] = [];
        occ.forEach((o: BreakOccurrenceApi) => {
          timeDayAllOccurrences.push({
            breakId: o.idbreak,
            occurrenceId: o.idoccurrence,
            priority: o.priority,
            startDate: moment(o.startdate),
          });
        });
        timeAllOccurrences.push(timeDayAllOccurrences);
      } else {
        timeAllOccurrences.push(null);
      }
    });
    allOccurrences.push({
      startHour: moment(time, 'HH:mm').format('LT'),
      hour24h: time,
      occurrences: timeAllOccurrences,
    });
  });

  return {
    breaks: allBreaks,
    occurrences: allOccurrences.sort((a, b) => a.hour24h.localeCompare(b.hour24h)),
  };
}

export interface BreakDetailsApi {
  status: string;
  data: {
    day_end_hour: string;
    day_start_hour: string;
    idbreak: number;
    idplaylist: number;
    idprogram: number;
    idradio: number;
    idtrack: number;
    item_type: BreakKind;
    nb_tracks: number;
    occurrence_interval: number;
    play_type: BreakPlayType;
    priority: number;
    repeat: number;
    repeat_daysofweek: string;
    repeat_end_date: string;
    repeat_start_date: string;
    stop_music: number;
    tolerance: number;
  };
}

export interface AddedBreakApi {
  day_end_hour: string;
  day_start_hour: string;
  idplaylist: number;
  idprogram: number;
  idtrack: number;
  item_type: BreakKind;
  nb_tracks: number;
  occurrence_interval: number;
  play_type: BreakPlayType;
  priority: number;
  repeat: number;
  repeat_daysofweek: string;
  repeat_end_date: string;
  repeat_start_date: string;
  stop_music: number;
  tolerance: number;
  idbreak?: number;
}

export function convertToBreak({ data }: BreakDetailsApi): BreakDetails {
  const breakObj: BreakDetails = {
    id: data.idbreak,
    kind: data.item_type,
    startHour: data.day_start_hour,
    startDay: moment(data.repeat_start_date),
    priority: data.priority,
    trackId: data.idtrack,
    programId: data.idprogram,
    playlistId: data.idplaylist,
    playType: data.play_type,
    fixedTime: !!data.stop_music,
    tolerance: data.tolerance,
    repeat: {
      repeatEndDay: moment(data.repeat_end_date),
      repeatDays: data.repeat_daysofweek,
    },
    repeatPerDay: {
      repeatEndHour: data.day_end_hour,
      repeatInterval: data.occurrence_interval,
    },
    dailyRepeat: !!data.repeat,
    tracksCount: data.nb_tracks,
  };
  return breakObj;
}

export function convertToAddedBreakApi(breakObj: BreakDetails): AddedBreakApi {
  const addedBreak: AddedBreakApi = {
    day_end_hour: breakObj.repeatPerDay.repeatEndHour ?? '23:59:59',
    day_start_hour: breakObj.startHour,
    idplaylist: breakObj.playlistId ?? null,
    idprogram: breakObj.programId ?? null,
    idtrack: breakObj.trackId ?? null,
    item_type: breakObj.kind,
    nb_tracks: breakObj.tracksCount,
    occurrence_interval: breakObj.repeatPerDay?.repeatInterval,
    play_type: breakObj.playType,
    priority: breakObj.priority,
    repeat: breakObj.dailyRepeat ? 1 : 0,
    repeat_daysofweek: breakObj.repeat?.repeatDays,
    repeat_end_date: (breakObj.repeat?.repeatEndDay ?? breakObj.startDay)
      .tz('UTC', true)
      .format('YYYY-MM-DD'),
    repeat_start_date: breakObj.startDay.tz('UTC', true).format('YYYY-MM-DD'),
    stop_music: breakObj.fixedTime ? 1 : 0,
    tolerance: breakObj.tolerance,
  };
  if (breakObj.id) {
    addedBreak.idbreak = breakObj.id;
  }
  return addedBreak;
}
